import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import vi from './locales/vi.json';
import en from './locales/en.json';
import zh from './locales/zh.json';
import policyVi from './locales/policies.vi.json';
import policyEn from './locales/policies.en.json';
import policyZh from './locales/policies.zh.json';
import legacyManagementEn from './locales/legacy-management.en.json';
import legacyManagementZh from './locales/legacy-management.zh.json';
import legacyManagementExtraEn from './locales/legacy-management-extra.en.json';
import legacyManagementExtraZh from './locales/legacy-management-extra.zh.json';
import legacyManagementExtra2En from './locales/legacy-management-extra2.en.json';
import legacyManagementExtra2Zh from './locales/legacy-management-extra2.zh.json';
import legacyManagementExtra3En from './locales/legacy-management-extra3.en.json';
import legacyManagementExtra3Zh from './locales/legacy-management-extra3.zh.json';
import legacyManagementExtra4En from './locales/legacy-management-extra4.en.json';
import legacyManagementExtra4Zh from './locales/legacy-management-extra4.zh.json';
import productDetailVi from './locales/product-detail.vi.json';
import productDetailEn from './locales/product-detail.en.json';
import productDetailZh from './locales/product-detail.zh.json';
import orderProductPopupVi from './locales/order-product-popup.vi.json';
import orderProductPopupEn from './locales/order-product-popup.en.json';
import orderProductPopupZh from './locales/order-product-popup.zh.json';

export const LANGUAGE_STORAGE_KEY = 'app_language';
export const DEFAULT_LANGUAGE = 'vi';
export const SUPPORTED_LANGUAGES = ['vi', 'en', 'zh'];

const dictionaries = {
  vi: { ...vi, productDetail: productDetailVi, orderProductPopup: orderProductPopupVi, policies: policyVi },
  en: { ...en, productDetail: productDetailEn, orderProductPopup: orderProductPopupEn, legacy: { ...(en.legacy || {}), ...legacyManagementEn, ...legacyManagementExtraEn, ...legacyManagementExtra2En, ...legacyManagementExtra3En, ...legacyManagementExtra4En }, policies: policyEn },
  zh: { ...zh, productDetail: productDetailZh, orderProductPopup: orderProductPopupZh, legacy: { ...(zh.legacy || {}), ...legacyManagementZh, ...legacyManagementExtraZh, ...legacyManagementExtra2Zh, ...legacyManagementExtra3Zh, ...legacyManagementExtra4Zh }, policies: policyZh },
};

export const normalizeLanguage = language => {
  const normalized = String(language || '').toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGES.indexOf(normalized) >= 0
    ? normalized
    : DEFAULT_LANGUAGE;
};

export const getCurrentLanguage = () => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  return normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
};

const initialLanguage = getCurrentLanguage();
delete axios.defaults.headers.common.lang;
delete axios.defaults.headers.common['Accept-Language'];

const getValue = (dictionary, key) =>
  key.split('.').reduce((value, part) =>
    value && Object.prototype.hasOwnProperty.call(value, part)
      ? value[part]
      : undefined, dictionary);

export const translate = (language, key, params = {}) => {
  const lang = normalizeLanguage(language);
  const value = getValue(dictionaries[lang], key) || getValue(dictionaries.vi, key) || key;
  if (typeof value !== 'string') return value;
  return Object.keys(params).reduce(
    (text, name) => text.replace(new RegExp(`{{${name}}}`, 'g'), params[name]),
    value,
  );
};

const I18nContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: key => key,
});

export const I18nProvider = ({ children }) => {
  const [language, updateLanguage] = useState(getCurrentLanguage);

  const setLanguage = nextLanguage => {
    const normalized = normalizeLanguage(nextLanguage);
    if (normalized === language) return;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    updateLanguage(normalized);
    // Reload so every screen refetches API-backed content with the new lang header.
    window.location.reload();
  };

  useEffect(() => {
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : language;
    delete axios.defaults.headers.common.lang;
    delete axios.defaults.headers.common['Accept-Language'];
  }, [language]);

  useEffect(() => {
    if (language === DEFAULT_LANGUAGE || typeof document === 'undefined') return undefined;
    const legacy = dictionaries[language].legacy || {};
    const phrases = Object.keys(legacy).sort((a, b) => b.length - a.length);
    if (!phrases.length) return undefined;

    const translateLegacyText = value => {
      if (!value || !value.trim()) return value;
      return phrases.reduce(
        (result, phrase) => result.split(phrase).join(legacy[phrase]),
        value,
      );
    };
    const translateNode = root => {
      if (!root) return;
      if (root.nodeType === Node.TEXT_NODE) {
        const parentTag = root.parentElement && root.parentElement.tagName;
        if (parentTag !== 'SCRIPT' && parentTag !== 'STYLE') {
          const translated = translateLegacyText(root.nodeValue);
          if (translated !== root.nodeValue) root.nodeValue = translated;
        }
        return;
      }
      if (root.nodeType !== Node.ELEMENT_NODE) return;
      ['placeholder', 'title', 'aria-label'].forEach(attribute => {
        if (root.hasAttribute(attribute)) {
          const value = root.getAttribute(attribute);
          const translated = translateLegacyText(value);
          if (translated !== value) root.setAttribute(attribute, translated);
        }
      });
      if (
        root.tagName === 'INPUT' &&
        ['button', 'submit', 'reset'].indexOf(String(root.type).toLowerCase()) >= 0
      ) {
        const translatedValue = translateLegacyText(root.value);
        if (translatedValue !== root.value) root.value = translatedValue;
      }
      Array.from(root.childNodes).forEach(translateNode);
    };
    translateNode(document.body);
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'characterData') translateNode(mutation.target);
        if (mutation.type === 'attributes') translateNode(mutation.target);
        mutation.addedNodes.forEach(translateNode);
      });
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder', 'title', 'aria-label', 'value'],
    });
    return () => observer.disconnect();
  }, [language]);

  return (
    <I18nContext.Provider
      value={{
        language,
        setLanguage,
        t: (key, params) => translate(language, key, params),
      }}
    >
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => useContext(I18nContext);

export const withTranslation = WrappedComponent => props => (
  <I18nContext.Consumer>
    {i18n => <WrappedComponent {...props} {...i18n} />}
  </I18nContext.Consumer>
);
